// Anonymous usage measurement: which screens are opened and which actions are
// taken, so we can see where people get stuck. An event is a name, the route,
// and a few fixed labels. It never carries anything from the plan (no ages,
// balances, income or results), no account identity, and no free text.
//
// Off in development, when the backend is not configured, and when the browser
// asks not to be tracked. Best-effort: a failed send is dropped silently.
import { supabase } from './api';
import { platform } from './platform';

const TABLE = 'usage_events';
const DEVICE_KEY = 'ultronic_device';
const MAX_QUEUE = 40;
const FLUSH_MS = 5000;
export const APP_VERSION = '1.0';

const uuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));

export function trackingDeclined(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  return nav.doNotTrack === '1' || nav.globalPrivacyControl === true;
}

export const OWNER_KEY = 'ultronic_owner';

/** True when `?notrack=1` is in the page URL (before or inside the hash route). */
export function notrackRequested(loc = typeof window !== 'undefined' ? window.location : {}) {
  const query = `${loc.search || ''}&${((loc.hash || '').split('?')[1]) || ''}`;
  return /(^|[?&])notrack=1(&|$)/.test(query);
}

/** Marks this browser as the owner's or a test run's, so it is never counted. */
export function markOwnerDevice() {
  try { localStorage.setItem(OWNER_KEY, '1'); } catch { /* storage unavailable */ }
}

/** Owner and QA browsers opt out: the stored flag, or `?notrack=1` (which is remembered). */
export function trackingOptedOut(loc) {
  if (notrackRequested(loc)) markOwnerDevice();
  try { return localStorage.getItem(OWNER_KEY) === '1'; } catch { return false; }
}

/** The admin dashboard is never counted. */
export function isExcludedPath(path) {
  return path === '/admin' || path.startsWith('/admin/');
}

const enabled = process.env.NODE_ENV === 'production' && !!supabase && !trackingDeclined();

// A random label for this installation, not derived from anything about the person.
function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return sessionId;
  }
}

const sessionId = uuid();
let queue = [];
let timer = null;

/** Keeps only short labels and numbers, so a plan value can never ride along by accident. */
export function safeProps(props = {}) {
  const out = {};
  Object.entries(props).slice(0, 6).forEach(([key, value]) => {
    if (typeof value === 'boolean') out[key] = value;
    else if (typeof value === 'string') out[key] = value.slice(0, 40);
  });
  return out;
}

function flush() {
  timer = null;
  if (queue.length === 0) return;
  const rows = queue;
  queue = [];
  supabase.from(TABLE).insert(rows).then(() => {}, () => {});
}

export function track(name, props) {
  if (!enabled || trackingOptedOut()) return;
  const path = (window.location.hash || '#/').slice(1, 120).split('?')[0];
  if (isExcludedPath(path)) return;
  queue.push({
    name: String(name).slice(0, 40),
    path,
    device_id: deviceId(),
    session_id: sessionId,
    platform,
    app_version: APP_VERSION,
    props: safeProps(props),
  });
  if (queue.length >= MAX_QUEUE) flush();
  else if (!timer) timer = setTimeout(flush, FLUSH_MS);
}

if (enabled && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
